import React, { useCallback, useEffect, useState } from 'react';
import { Card } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (
  window.location.hostname === 'localhost' ? 'http://localhost:8001' : 'https://api.heytraveler.ai'
);
const STATUSES = ['pending', 'approved', 'rejected', 'revoked'];

function AdminPanel() {
  const [adminKey, setAdminKey] = useState('');
  const [actor, setActor] = useState('heytraveler-admin');
  const [authenticated, setAuthenticated] = useState(false);
  const [status, setStatus] = useState('pending');
  const [jobs, setJobs] = useState([]);
  const [edits, setEdits] = useState({});
  const [error, setError] = useState('');

  const headers = useCallback((json = false) => ({
    admin_key: adminKey,
    'x-admin-actor': actor || 'heytraveler-admin',
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  }), [adminKey, actor]);

  const loadJobs = useCallback(async () => {
    if (!adminKey) return;
    setError('');
    const response = await fetch(`${BACKEND_URL}/api/admin/jobs?approval_status=${status}&limit=200`, {
      headers: headers(),
    });
    const data = await response.json();
    if (!response.ok) {
      setAuthenticated(false);
      setError(typeof data.detail === 'string' ? data.detail : 'could not load review queue');
      return;
    }
    setAuthenticated(true);
    setJobs(data.jobs || []);
  }, [adminKey, headers, status]);

  useEffect(() => { if (authenticated) loadJobs(); }, [authenticated, loadJobs]);

  const patchJob = (jobId, field, value) => {
    setEdits(current => ({
      ...current,
      [jobId]: { ...(current[jobId] || {}), [field]: field === 'pay_weekly_gross' ? Number(value) : value },
    }));
  };

  const request = async (path, method = 'POST', body = {}) => {
    setError('');
    const response = await fetch(`${BACKEND_URL}${path}`, {
      method,
      headers: headers(true),
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      const detail = data.detail;
      setError(typeof detail === 'string' ? detail : detail?.message || 'review action failed');
      return false;
    }
    setEdits({});
    await loadJobs();
    return true;
  };

  if (!authenticated) {
    return (
      <main className="min-h-screen bg-[#FBFBF9] flex items-center justify-center p-4">
        <Card className="p-6 w-full max-w-md space-y-4">
          <div>
            <h1 className="text-2xl font-semibold text-[#3B3A39]">Care Team job review</h1>
            <p className="text-sm text-[#6B6A68] mt-1">Every XML job stays hidden until a HeyTraveler human approves it.</p>
          </div>
          <Input type="password" placeholder="admin API key" value={adminKey} onChange={event => setAdminKey(event.target.value)} />
          <Input placeholder="reviewer name" value={actor} onChange={event => setActor(event.target.value)} />
          {error && <p className="text-sm text-red-700">{error}</p>}
          <Button className="w-full" onClick={loadJobs}>open review queue</Button>
        </Card>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FBFBF9] py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-[#3B3A39]">Care Team approval queue</h1>
            <p className="text-sm text-[#6B6A68] mt-1">Edits revoke publication and require a fresh approval.</p>
          </div>
          <Input className="max-w-xs" value={actor} onChange={event => setActor(event.target.value)} aria-label="reviewer name" />
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {STATUSES.map(value => (
            <Button key={value} variant={status === value ? 'default' : 'outline'} onClick={() => setStatus(value)}>
              {value}
            </Button>
          ))}
        </div>
        {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-800 text-sm">{error}</div>}
        {jobs.length === 0 && <Card className="p-8 text-center text-[#6B6A68]">No {status} Care Team jobs.</Card>}

        <div className="space-y-4">
          {jobs.map(job => {
            const draft = edits[job._id] || {};
            const value = field => draft[field] ?? job[field] ?? '';
            return (
              <Card key={job._id} className="p-5 border-[#E6E6E6]">
                <div className="flex flex-wrap justify-between gap-3 mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-[#8B8A88]">{job.source_job_id || job.requisition_id}</div>
                    <h2 className="text-lg font-semibold text-[#3B3A39]">{job.title}</h2>
                    <p className="text-xs text-[#6B6A68]">source {job.source_status} · quality {job.quality_status} · approval {job.approval_status}</p>
                  </div>
                  {job.quality_issues?.length > 0 && (
                    <div className="text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2">{job.quality_issues.join(', ')}</div>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <Input value={value('specialty_normalized')} onChange={event => patchJob(job._id, 'specialty_normalized', event.target.value)} placeholder="normalized specialty" />
                  <Input value={value('profession')} onChange={event => patchJob(job._id, 'profession', event.target.value)} placeholder="profession" />
                  <Input value={value('pay_weekly_gross')} onChange={event => patchJob(job._id, 'pay_weekly_gross', event.target.value)} type="number" placeholder="gross weekly pay" />
                  <Input value={value('city')} onChange={event => patchJob(job._id, 'city', event.target.value)} placeholder="city" />
                  <Input value={value('state')} onChange={event => patchJob(job._id, 'state', event.target.value)} placeholder="state" />
                  <Input value={value('shift')} onChange={event => patchJob(job._id, 'shift', event.target.value)} placeholder="shift (optional)" />
                  <Input value={value('start_date')} onChange={event => patchJob(job._id, 'start_date', event.target.value)} placeholder="start date (optional)" />
                  <Input className="sm:col-span-2" value={value('scheduling_link')} onChange={event => patchJob(job._id, 'scheduling_link', event.target.value)} placeholder="Care Team source URL" />
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  {Object.keys(draft).length > 0 && (
                    <Button variant="outline" onClick={() => request(`/api/admin/jobs/${job._id}`, 'PATCH', draft)}>save corrections</Button>
                  )}
                  {job.approval_status === 'pending' && Object.keys(draft).length === 0 && (
                    <Button onClick={() => request(`/api/admin/jobs/${job._id}/approve`)}>approve current version</Button>
                  )}
                  {job.approval_status === 'pending' && Object.keys(draft).length > 0 && (
                    <span className="text-xs text-amber-800 self-center">save corrections before approving</span>
                  )}
                  {job.approval_status === 'pending' && (
                    <Button variant="outline" onClick={() => request(`/api/admin/jobs/${job._id}/reject`, 'POST', { reason: 'not approved for alpha' })}>reject</Button>
                  )}
                  {job.approval_status === 'approved' && (
                    <Button variant="destructive" onClick={() => request(`/api/admin/jobs/${job._id}/revoke`, 'POST', { reason: 'revoked by reviewer' })}>revoke</Button>
                  )}
                  <a href={job.scheduling_link} target="_blank" rel="noreferrer" className="text-sm underline text-[#2F7D5C] self-center">verify source</a>
                </div>

                {job.approval_history?.length > 0 && (
                  <details className="mt-4 text-xs text-[#6B6A68]">
                    <summary className="cursor-pointer">approval audit ({job.approval_history.length})</summary>
                    <ul className="mt-2 space-y-1">
                      {job.approval_history.slice().reverse().map((entry, index) => (
                        <li key={index}>{entry.action} · {entry.actor} · {entry.at}{entry.reason ? ` · ${entry.reason}` : ''}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </main>
  );
}

export default AdminPanel;
